import { inject, type Ref } from 'vue';
import { importLibrary } from '@/utils/google-maps-loader';
import { type SerializedUser } from '@/api/users';
import { useCompanyMapCenter, COUNTRY_ZOOM, type LatLng } from '@/composables/useCompanyMapCenter';

const PLACE_ZOOM = 18;

export interface GeocodeResult {
  address?: string;
  latitude?: string;
  longitude?: string;
}

// Google Places autocomplete + click-to-place map picker, shared by the address forms.
// Callbacks receive only the fields that resolved, so callers assign conditionally and never
// clobber existing values with blanks.
export function useAddressGeocoding() {
  const currentUser = inject<Ref<SerializedUser | undefined>>('currentUser');
  const companyMapCenter = useCompanyMapCenter();

  // Restricts suggestions to the company's country. A company whose country has no ISO code
  // ("Otro") gets worldwide suggestions rather than a silent fallback to Chile.
  function autocompleteOptions(): google.maps.places.AutocompleteOptions {
    const country = currentUser?.value?.companyCountryCode;

    return country ? { componentRestrictions: { country } } : {};
  }

  // Resolves to a disposer that detaches the listeners and removes the `.pac-container` dropdown
  // Google appends to <body>. Callers with a transient input (e.g. a dialog) should call it on
  // close/unmount to avoid leaking detached nodes; long-lived forms can ignore it.
  async function attachAutocomplete(
    inputId: string, onPlace: (result: GeocodeResult) => void,
  ): Promise<() => void> {
    const places = await importLibrary('places');
    const input = document.getElementById(inputId) as HTMLInputElement | null;
    if (!input) return () => {};

    const autocomplete = new places.Autocomplete(input, autocompleteOptions());

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      const location = place.geometry?.location;
      onPlace({
        address: place.formatted_address,
        latitude: location?.lat().toString(),
        longitude: location?.lng().toString(),
      });
    });

    return () => {
      google.maps.event.clearInstanceListeners(autocomplete);
      document.querySelectorAll('.pac-container').forEach((node) => node.remove());
    };
  }

  // Without a `position` (no coordinates yet) the picker opens over the company's country and
  // shows no marker until the first click.
  async function openMapPicker(
    mapElementId: string,
    position: LatLng | null,
    onPick: (result: GeocodeResult) => void,
  ): Promise<void> {
    const { Map } = await importLibrary('maps');
    const { AdvancedMarkerElement } = await importLibrary('marker');
    const { Geocoder } = await importLibrary('geocoding');

    const mapElement = document.getElementById(mapElementId) as HTMLElement | null;
    if (!mapElement) return;

    const map = new Map(mapElement, {
      center: position ?? companyMapCenter.value,
      zoom: position ? PLACE_ZOOM : COUNTRY_ZOOM,
      mapId: import.meta.env.VITE_GOOGLE_MAPS_MAP_ID,
    });
    const marker = new AdvancedMarkerElement({ map, position: position ?? undefined });
    const geocoder = new Geocoder();

    map.addListener('click', (event: google.maps.MapMouseEvent) => {
      const newPosition = event.latLng;
      if (!newPosition) return;
      marker.position = newPosition;
      geocoder.geocode({ location: newPosition }, (results, status) => {
        onPick({
          address: status === 'OK' && results?.[0] ? results[0].formatted_address : undefined,
          latitude: newPosition.lat().toString(),
          longitude: newPosition.lng().toString(),
        });
      });
    });
  }

  return { attachAutocomplete, openMapPicker };
}
